import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { UserRole } from "../types/User";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
// import { Separator } from "./ui/separator";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import {
  Plus,
  FileText,
  Users,
  CheckCircle,
  Clock,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import "./Dashboard.css";
import { getBackendUrl } from "../config/env";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  // const { surveys } = useSurvey();
  const { user, hasPermission, centerInfo, selectedCenterId } = useAuth();
  const [realSurveys, setRealSurveys] = useState<any[]>([]);
  const [totalResponses, setTotalResponses] = useState(0);
  const [activeSurveys, setActiveSurveys] = useState(0);
  const [averageCompletionRate, setAverageCompletionRate] = useState(0);
  const [recentResponses, setRecentResponses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const canCreateSurvey = hasPermission([
    UserRole.SYSTEM_ADMIN,
    UserRole.GENERAL_ADMIN,
  ]);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCenterId]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;

      // 설문조사 목록 가져오기 (대시보드용으로 모든 데이터 필요)
      const params = new URLSearchParams({
        page: "1",
        limit: "1000", // 충분히 큰 수로 설정하여 모든 설문조사 가져오기
      });
      
      if (centerUserId) {
        params.append("centerUserId", centerUserId);
      }

      const surveysUrl = `${getBackendUrl()}/api/surveys?${params.toString()}`;

      console.log("🔍 대시보드 API 요청:", surveysUrl);
      
      const surveysResponse = await fetch(surveysUrl);
      if (surveysResponse.ok) {
        const surveysData = await surveysResponse.json();
        
        console.log("📊 받은 설문조사 데이터:", surveysData);
        
        // 페이징 응답과 비-페이징 응답 모두 처리
        const surveys = surveysData.data ? surveysData.data : surveysData;
        console.log("📋 처리된 설문조사 목록:", surveys);
        
        setRealSurveys(surveys);

        // 활성 설문 수 계산
        const active = surveys.filter(
          (s: any) => s.status === "published" || s.isPublished
        ).length;
        console.log("🟢 활성 설문 수:", active);
        console.log("📈 총 설문 수:", surveys.length);
        
        setActiveSurveys(active);

        // 각 설문의 응답 가져와서 통계 계산
        let totalResponseCount = 0;
        let completedResponses = 0;
        let allResponses: any[] = [];

        for (const survey of surveys) {
          try {
            const surveyId = survey.id || survey._id;
            // 모든 응답을 가져오기 위해 limit을 크게 설정
            const responseParams = new URLSearchParams({
              limit: "1000", // 충분히 큰 수로 설정하여 모든 응답 가져오기
            });
            
            if (centerUserId) {
              responseParams.append("centerUserId", centerUserId);
            }
            
            const responseUrl = `${getBackendUrl()}/api/responses/survey/${surveyId}?${responseParams.toString()}`;

            const responseData = await fetch(responseUrl);
            if (responseData.ok) {
              const result = await responseData.json();
              // Handle paginated response format
              let responses = [];
              let totalCount = 0;

              if (result.data && Array.isArray(result.data)) {
                // Paginated response
                responses = result.data;
                totalCount = result.total || responses.length;
              } else if (Array.isArray(result)) {
                // Non-paginated response
                responses = result;
                totalCount = responses.length;
              }

              totalResponseCount += totalCount;

              // 완료된 응답 수 계산
              const completed = responses.filter(
                (r: any) => r.status === "completed"
              ).length;
              completedResponses += completed;

              // 응답에 설문 제목 추가
              const responsesWithSurvey = responses.map((r: any) => ({
                ...r,
                surveyTitle: survey.title,
                surveyId: surveyId,
              }));
              allResponses = [...allResponses, ...responsesWithSurvey];
            }
          } catch (error) {
            console.error(
              `Error loading responses for survey ${survey.id}:`,
              error
            );
          }
        }

        console.log("📊 총 응답 수:", totalResponseCount);
        console.log("✅ 완료된 응답 수:", completedResponses);
        
        setTotalResponses(totalResponseCount);

        // 평균 완료율 계산
        if (totalResponseCount > 0) {
          const rate = Math.round(
            (completedResponses / totalResponseCount) * 100
          );
          setAverageCompletionRate(rate);
          console.log("📈 평균 완료율:", rate + "%");
        } else {
          setAverageCompletionRate(0);
          console.log("📈 평균 완료율: 0% (응답 없음)");
        }

        // 최근 응답 5개 정렬
        const sortedResponses = allResponses
          .sort((a, b) => {
            const dateA = new Date(a.completedAt || a.createdAt || 0).getTime();
            const dateB = new Date(b.completedAt || b.createdAt || 0).getTime();
            return dateB - dateA;
          })
          .slice(0, 5);
        console.log("🕒 최근 응답:", sortedResponses);
        
        setRecentResponses(sortedResponses);
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
      console.log("🏁 대시보드 데이터 로딩 완료");
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "날짜 없음";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "날짜 없음";
    return new Intl.DateTimeFormat("ko-KR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const handleResponseClick = (surveyId: string, responseId: string) => {
    navigate(`/survey-responses?surveyId=${surveyId}&responseId=${responseId}`);
  };

  const StatCard = ({
    icon: Icon,
    title,
    value,
    description,
    loading,
  }: any) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            <div className="h-7 bg-muted animate-pulse rounded" />
            <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
          </div>
        ) : (
          <>
            <div className="text-2xl font-bold">{value}</div>
            <p className="text-xs text-muted-foreground">{description}</p>
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="dashboard-container p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">대시보드</h1>
          <p className="text-muted-foreground">
            {centerInfo?.centerName || "전체"} 센터 통계
          </p>
        </div>
        {canCreateSurvey && (
          <Button onClick={() => navigate("/survey-builder")} className="gap-2">
            <Plus className="h-4 w-4" />새 설문 작성
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={FileText}
          title="전체 설문"
          value={realSurveys.length}
          description="등록된 설문 수"
          loading={loading}
        />
        <StatCard
          icon={CheckCircle}
          title="활성 설문"
          value={activeSurveys}
          description="진행 중인 설문"
          loading={loading}
        />
        <StatCard
          icon={Users}
          title="총 응답"
          value={totalResponses}
          description="전체 응답 수"
          loading={loading}
        />
        <StatCard
          icon={Clock}
          title="완료율"
          value={`${averageCompletionRate}%`}
          description="평균 완료율"
          loading={loading}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>최근 설문</CardTitle>
            <CardDescription>최근 생성된 설문 목록</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-4 border rounded-lg animate-pulse"
                  >
                    <div className="space-y-2 flex-1">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : realSurveys.length > 0 ? (
              <div className="space-y-4">
                {realSurveys.slice(0, 5).map((survey) => (
                  <div
                    key={survey.id || survey._id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors cursor-pointer"
                    onClick={() =>
                      navigate(`/survey-list?id=${survey.id || survey._id}`)
                    }
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {survey.title}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(survey.createdAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={survey.isPublished ? "default" : "secondary"}
                      >
                        {survey.isPublished ? "게시됨" : "초안"}
                      </Badge>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>설문이 없습니다</AlertTitle>
                <AlertDescription>
                  아직 생성된 설문이 없습니다.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>최근 응답</CardTitle>
            <CardDescription>최근 제출된 응답 목록</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-4 border rounded-lg animate-pulse"
                  >
                    <div className="space-y-2 flex-1">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentResponses.length > 0 ? (
              <div className="space-y-4">
                {recentResponses.map((response) => (
                  <div
                    key={response.id || response._id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors cursor-pointer"
                    onClick={() =>
                      handleResponseClick(
                        response.surveyId,
                        response.id || response._id
                      )
                    }
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {response.surveyTitle || "제목 없음"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {response.respondent?.name ||
                          response.respondentName ||
                          "익명"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          response.status === "completed"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {response.status === "completed" ? "완료" : "진행중"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(response.completedAt || response.createdAt)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>응답이 없습니다</AlertTitle>
                <AlertDescription>
                  아직 제출된 응답이 없습니다.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
